import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link as RouterLink } from 'react-router-dom';
import { getStoredUser } from './auth';
import { PageHeader } from './components/page-elements';

const quickLinks = [
  { label: 'Receivers', to: '/receivers' },
  { label: 'Locations', to: '/projects' },
  { label: 'Shippers', to: '/shippers' },
  { label: 'Materials', to: '/materials' },
  { label: 'Orders', to: '/orders' },
  { label: 'BOLs', to: '/bols' },
  { label: 'Railcars', to: '/railcars' },
  { label: 'Profile', to: '/profile' },
];

const CustomerHelp = () => {
  const user = getStoredUser();
  const isCustomer = user?.role === 'customer';

  return (
    <Box p={2}>
      <PageHeader
        title="Customer Help Center"
        subtitle="Setup guides and workflow tips for day-to-day transload operations."
      />

      {!isCustomer && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This page is primarily intended for customer users.
        </Alert>
      )}

      <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Quick Navigation
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {quickLinks.map((item) => (
              <Chip
                key={item.to}
                label={item.label}
                component={RouterLink}
                to={item.to}
                clickable
                variant="outlined"
              />
            ))}
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={1.5}>
        <Grid item xs={12}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>1. Initial Setup Order (Recommended)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                Create records in this order so dropdowns are ready when you create truck orders:
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>1. Add Receivers under <strong>Receivers</strong>.</Typography>
              <Typography variant="body2">2. Add one or more Locations under each Receiver in <strong>Locations</strong>.</Typography>
              <Typography variant="body2">3. Add Shippers in <strong>Shippers</strong>.</Typography>
              <Typography variant="body2">4. Add Materials in <strong>Materials</strong>.</Typography>
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>2. How To Add Receivers and Locations</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                Receivers hold the billing address and can have many Locations. Open <strong>Receivers</strong> to create or edit a Receiver, then use the Receiver action to add Locations tied to that Receiver.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Tip: Keep receiver names and address formatting consistent to avoid duplicate records.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>3. How To Add Shippers and Materials</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                Use <strong>Shippers</strong> to maintain pickup party contacts and <strong>Materials</strong> to maintain product names and reference numbers.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Materials with a reference number will display both values in order forms for easier selection.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>4. Order Workflow</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">1. Go to <strong>Orders</strong> and create a new order.</Typography>
              <Typography variant="body2">2. Choose material, receiver location, shipper, and preferred railcar (or “Determined by Site”).</Typography>
              <Typography variant="body2">3. Save as Draft or Submit.</Typography>
              <Typography variant="body2">4. Draft orders can be edited; submitted orders can still be canceled until completed.</Typography>
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>5. BOL and Railcar Visibility</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                Track unload activity in <strong>BOLs</strong> and monitor railcars in <strong>Railcars</strong>. Railcar data shown to customer users is scoped to that customer.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                If a railcar is released empty, use the release action on available cars when prompted.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700 }}>6. Profile and Password Help</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                Use <strong>Profile</strong> to review user information and preferences. If login fails, use <strong>Forgot Password</strong> on the login page.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                If reset emails are delayed, contact your internal administrator to verify your account email.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CustomerHelp;
